import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { makeStyles, Box, Typography, Chip } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

import { closeCashShift } from '../../../../services/merchantsService';

import { useToggle } from '../../../hooks/useToggle';
import { useLazyRequest } from '../../../hooks/useRequest';
import { formatCurrencyValue } from '../../../../utils';

import ButtonWithCirculaProgress from '../../ButtonWithCirculaProgress';
import DialogConfirmation from '../../DialogConfirmation';

import styles from './styles';

const useStyles = makeStyles(styles);

const ShiftWidgetCloseSection = ({ currencies, onCompleted }) => {
  const { t } = useTranslation('common');
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();

  const [
    showConfirmationCloseShift,
    toggleShowConfirmationCloseShift,
  ] = useToggle(false);

  const [, loadingCloseCashShift, , requestCloseCashShift] = useLazyRequest({
    request: closeCashShift,
    withPostSuccess: (response) => {
      toggleShowConfirmationCloseShift(false);

      enqueueSnackbar(t('payments.shift.closeCash.successMessage'), {
        variant: 'success',
      });

      onCompleted(response.data);
    },
    withPostFailure: (response) => {
      const message =
        response.errorData.message ??
        t('payments.shift.closeCash.errorMessage');

      enqueueSnackbar(message, { variant: 'error' });
    },
  });

  const handleCloseCashShift = useCallback(async () => {
    await requestCloseCashShift();
  }, [requestCloseCashShift]);

  return (
    <Box className={classes.container}>
      <Typography variant="subtitle1" className={classes.title}>
        {t('payments.shift.closeCash.totalsCharged')}
      </Typography>

      <Box className={classes.currenciesContainer}>
        {currencies.length === 0 && (
          <Typography variant="body2" className={classes.noCurrenciesText}>
            {t('payments.shift.closeCash.noPaymentsRecorded')}
          </Typography>
        )}

        {currencies.map((currency) => (
          <Chip
            key={`currency-${currency.currency_id}`}
            label={
              <>
                <b>{currency.currency_id.toUpperCase()}:</b>{' '}
                {formatCurrencyValue(currency.amount, { style: 'decimal' })}
              </>
            }
            variant="outlined"
            className={classes.currencyChip}
          />
        ))}
      </Box>

      <ButtonWithCirculaProgress
        variant="contained"
        color="secondary"
        fullWidth
        loading={false}
        label={t('payments.shift.closeCash.title')}
        className={classes.button}
        handleButtonClick={() => toggleShowConfirmationCloseShift(true)}
      />

      <DialogConfirmation
        open={showConfirmationCloseShift}
        description={t('payments.shift.closeCash.confirmMessage')}
        onConfirm={handleCloseCashShift}
        onCancel={() => toggleShowConfirmationCloseShift(false)}
        loadingConfirm={loadingCloseCashShift}
      />
    </Box>
  );
};

ShiftWidgetCloseSection.propTypes = {
  currencies: PropTypes.arrayOf(
    PropTypes.shape({
      currency_id: PropTypes.string,
      amount: PropTypes.string,
    }),
  ),
  onCompleted: PropTypes.func.isRequired,
};

ShiftWidgetCloseSection.defaultProps = {
  currencies: [],
};

export default ShiftWidgetCloseSection;
